import { useState } from 'react';
import { commissionScenarios } from '../data';

const scenarioIcons: Record<number, string> = {
  0: 'ri-flashlight-line',
  1: 'ri-line-chart-line',
  2: 'ri-calendar-line',
};

const scenarioColors: Record<number, { active: string; glow: string; accent: string }> = {
  0: { active: 'bg-accent-500', glow: 'bg-accent-500/10', accent: 'text-accent-400' },
  1: { active: 'bg-primary-500', glow: 'bg-primary-500/10', accent: 'text-primary-400' },
  2: { active: 'bg-secondary-500', glow: 'bg-secondary-500/10', accent: 'text-secondary-400' },
};

export default function CommissionCalculationSection() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [expandedBar, setExpandedBar] = useState<'commission' | 'spread' | null>(null);

  const scenario = commissionScenarios[activeScenario];
  const monthlyTrades = scenario.tradesPerMonth;
  const lotSize = scenario.lotSize;
  const commissionPerSide = scenario.commissionPerSide;
  const spreadCostPerTrade = scenario.spreadCostPerTrade;

  const commissionRT = commissionPerSide * 2;
  const monthlyCommission = monthlyTrades * lotSize * commissionRT;
  const monthlySpread = monthlyTrades * lotSize * spreadCostPerTrade;
  const monthlyTotal = monthlyCommission + monthlySpread;
  const annualTotal = monthlyTotal * 12;
  const commissionPct = monthlyTotal > 0 ? Math.round((monthlyCommission / monthlyTotal) * 100) : 0;

  const toggleBar = (bar: 'commission' | 'spread') => {
    setExpandedBar(prev => (prev === bar ? null : bar));
  };

  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">

        {/* Section header */}
        <div className="max-w-[640px] mb-8 md:mb-16">
          <h2 className="text-[26px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-3 md:mb-4">
            How commission adds up — in real dollars
          </h2>
          <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-600">
            Pick a trading style below to see exactly how commission stacks against spread over a month of trading.
          </p>
        </div>

        {/* ─── Scenario selector ─── */}

        {/* Mobile: segmented control */}
        <div className="sm:hidden mb-6">
          <div className="flex rounded-xl bg-background-100 border border-background-200 p-1 gap-1">
            {commissionScenarios.map((s, idx) => {
              const isActive = activeScenario === idx;
              return (
                <button
                  key={s.label}
                  onClick={() => { setActiveScenario(idx); setExpandedBar(null); }}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 px-2 rounded-lg transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-foreground-950 text-background-50'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${scenarioIcons[idx]} w-4 h-4 flex items-center justify-center text-[15px]`} />
                  <span className="text-[11px] font-[650] leading-tight text-center">{s.label}</span>
                </button>
              );
            })}
          </div>

          {/* Mobile: active scenario params */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {[
              { icon: 'ri-swap-line', label: `${commissionScenarios[activeScenario].tradesPerMonth} trades/mo` },
              { icon: 'ri-stack-line', label: `${commissionScenarios[activeScenario].lotSize} lots/trade` },
              { icon: 'ri-coins-line', label: `$${commissionScenarios[activeScenario].commissionPerSide.toFixed(2)}/side` },
            ].map(item => (
              <span key={item.label} className="inline-flex items-center gap-1.5 text-[12px] text-foreground-500">
                <i className={`${item.icon} w-3.5 h-3.5 flex items-center justify-center text-foreground-400`} />
                {item.label}
              </span>
            ))}
          </div>
        </div>

        {/* Desktop: full scenario cards */}
        <div className="hidden sm:grid sm:grid-cols-3 gap-4 md:gap-5 mb-8">
          {commissionScenarios.map((s, idx) => {
            const color = scenarioColors[idx];
            const isActive = activeScenario === idx;
            return (
              <button
                key={s.label}
                onClick={() => { setActiveScenario(idx); setExpandedBar(null); }}
                className={`group relative text-left rounded-card border-2 transition-all duration-300 cursor-pointer overflow-hidden ${
                  isActive
                    ? 'border-accent-500 shadow-[0_0_0_4px_oklch(var(--accent-100))]'
                    : 'border-background-200 hover:border-foreground-300 hover:-translate-y-1'
                }`}
              >
                {isActive && <div className={`absolute inset-0 ${color.glow} pointer-events-none`} />}
                <div className="relative p-5 md:p-6">
                  <div className="text-[16px] font-[700] text-foreground-950 mb-3">{s.label}</div>
                  <div className="space-y-1.5 text-[12px] text-foreground-500">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-swap-line w-3.5 h-3.5 flex items-center justify-center" />
                      <span>{s.tradesPerMonth} trades/month</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-stack-line w-3.5 h-3.5 flex items-center justify-center" />
                      <span>{s.lotSize} lots/trade</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-coins-line w-3.5 h-3.5 flex items-center justify-center" />
                      <span>${s.commissionPerSide.toFixed(2)}/side</span>
                    </div>
                  </div>
                  {isActive && <div className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-accent-500" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* ─── Result dark card ─── */}
        <div className="relative bg-foreground-950 rounded-card overflow-hidden">
          <div className="absolute top-0 right-0 w-[280px] h-[280px] bg-accent-500/8 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-accent-500/5 rounded-full blur-[80px] pointer-events-none" />

          <div className="relative p-5 md:p-10">

            {/* Scenario label + description */}
            <div className="flex items-center gap-2.5 mb-2">
              <span className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex-shrink-0 flex items-center justify-center ${scenarioColors[activeScenario].active} text-background-50`}>
                <i className={`${scenarioIcons[activeScenario]} w-3.5 h-3.5 flex items-center justify-center`} />
              </span>
              <span className="text-[14px] md:text-[15px] font-[700] text-background-50">{scenario.label}</span>
            </div>
            <p className="text-[12px] md:text-[14px] leading-relaxed text-foreground-400 mb-6 md:mb-10">
              {scenario.description}
            </p>

            {/* Mobile: cost summary block */}
            <div className="sm:hidden mb-6">
              {/* Monthly big number */}
              <div className="mb-3">
                <span className="text-[10px] font-[650] text-foreground-500 uppercase tracking-widest block mb-1">Monthly Cost</span>
                <div className="flex items-end gap-3">
                  <span className="text-[56px] font-[800] leading-none text-background-50 tracking-tight">
                    ${monthlyTotal.toFixed(0)}
                  </span>
                  <span className="text-[13px] text-foreground-500 pb-2">/ month</span>
                </div>
              </div>
              {/* Annual pill */}
              <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-accent-500/15 border border-accent-500/25">
                <i className="ri-calendar-2-line w-4 h-4 flex items-center justify-center text-accent-400" />
                <div>
                  <span className="text-[10px] font-[650] text-accent-400 uppercase tracking-wider block leading-none mb-0.5">Annual projection</span>
                  <span className="text-[20px] font-[750] text-background-50 tracking-tight leading-none">${annualTotal.toFixed(0)}</span>
                </div>
              </div>
            </div>

            {/* Desktop: two-column layout */}
            <div className="hidden sm:flex flex-col lg:flex-row gap-8 lg:gap-14">
              {/* Left: Monthly cost */}
              <div className="lg:w-[340px] lg:flex-shrink-0">
                <div className="bg-foreground-900/60 rounded-card p-6 md:p-8">
                  <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-3 block">
                    Monthly Cost
                  </span>
                  <span className="text-[72px] md:text-[88px] font-[800] leading-none text-background-50 tracking-tight block mb-1">
                    ${monthlyTotal.toFixed(0)}
                  </span>
                  <p className="text-[14px] text-foreground-400 mb-6">
                    Total per month — commission + spread
                  </p>
                  <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20">
                    <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider block mb-1">Annual Projection</span>
                    <span className="text-[28px] font-[750] text-background-50 tracking-tight block">${annualTotal.toFixed(0)}</span>
                    <span className="text-[12px] text-foreground-400 mt-0.5 block">Extrapolated from monthly activity</span>
                  </div>
                </div>
              </div>
              {/* Right: bars — desktop uses hover */}
              <div className="flex-1 flex flex-col justify-center space-y-6">
                <BarRow
                  label="Commission"
                  color="bg-accent-500"
                  labelColor="text-accent-400"
                  borderColor="border-accent-500/20"
                  amount={monthlyCommission}
                  pct={commissionPct}
                  formula={`${monthlyTrades} trades × ${lotSize} lots × $${commissionRT.toFixed(2)} round-turn`}
                  isOpen={expandedBar === 'commission'}
                  onToggle={() => toggleBar('commission')}
                  detail={[
                    { label: 'Commission per side', value: `$${commissionPerSide.toFixed(2)}` },
                    { label: 'Round-turn (×2 sides)', value: `$${commissionRT.toFixed(2)}` },
                    { label: `Per trade (${lotSize} lots × $${commissionRT.toFixed(2)})`, value: `$${(lotSize * commissionRT).toFixed(2)}` },
                    { label: `${monthlyTrades} trades × $${(lotSize * commissionRT).toFixed(2)}`, value: `$${monthlyCommission.toFixed(0)}/month`, highlight: true },
                  ]}
                />
                <BarRow
                  label="Spread"
                  color="bg-primary-500"
                  labelColor="text-primary-400"
                  borderColor="border-primary-500/20"
                  amount={monthlySpread}
                  pct={100 - commissionPct}
                  formula={`${monthlyTrades} trades × ${lotSize} lots × $${spreadCostPerTrade.toFixed(2)}/trade`}
                  isOpen={expandedBar === 'spread'}
                  onToggle={() => toggleBar('spread')}
                  detail={[
                    { label: 'Spread cost per trade', value: `$${spreadCostPerTrade.toFixed(2)}` },
                    { label: `Per trade (${lotSize} lots × $${spreadCostPerTrade.toFixed(2)})`, value: `$${(lotSize * spreadCostPerTrade).toFixed(2)}` },
                    { label: `${monthlyTrades} trades × $${(lotSize * spreadCostPerTrade).toFixed(2)}`, value: `$${monthlySpread.toFixed(0)}/month`, highlight: true },
                  ]}
                />
                <InsightBlock commissionPct={commissionPct} />
              </div>
            </div>

            {/* Mobile: bars stacked */}
            <div className="sm:hidden space-y-4">
              <BarRow
                label="Commission"
                color="bg-accent-500"
                labelColor="text-accent-400"
                borderColor="border-accent-500/20"
                amount={monthlyCommission}
                pct={commissionPct}
                formula={`${monthlyTrades} trades × ${lotSize} lots × $${commissionRT.toFixed(2)} RT`}
                isOpen={expandedBar === 'commission'}
                onToggle={() => toggleBar('commission')}
                detail={[
                  { label: 'Commission per side', value: `$${commissionPerSide.toFixed(2)}` },
                  { label: 'Round-turn (×2)', value: `$${commissionRT.toFixed(2)}` },
                  { label: `Per trade (${lotSize} lots × $${commissionRT.toFixed(2)})`, value: `$${(lotSize * commissionRT).toFixed(2)}` },
                  { label: `${monthlyTrades} trades × $${(lotSize * commissionRT).toFixed(2)}`, value: `$${monthlyCommission.toFixed(0)}/mo`, highlight: true },
                ]}
              />
              <BarRow
                label="Spread"
                color="bg-primary-500"
                labelColor="text-primary-400"
                borderColor="border-primary-500/20"
                amount={monthlySpread}
                pct={100 - commissionPct}
                formula={`${monthlyTrades} trades × ${lotSize} lots × $${spreadCostPerTrade.toFixed(2)}/trade`}
                isOpen={expandedBar === 'spread'}
                onToggle={() => toggleBar('spread')}
                detail={[
                  { label: 'Spread cost per trade', value: `$${spreadCostPerTrade.toFixed(2)}` },
                  { label: `Per trade (${lotSize} lots × $${spreadCostPerTrade.toFixed(2)})`, value: `$${(lotSize * spreadCostPerTrade).toFixed(2)}` },
                  { label: `${monthlyTrades} trades × $${(lotSize * spreadCostPerTrade).toFixed(2)}`, value: `$${monthlySpread.toFixed(0)}/mo`, highlight: true },
                ]}
              />
              <InsightBlock commissionPct={commissionPct} />
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Shared sub-components ─── */

interface DetailRow {
  label: string;
  value: string;
  highlight?: boolean;
}

interface BarRowProps {
  label: string;
  color: string;
  labelColor: string;
  borderColor: string;
  amount: number;
  pct: number;
  formula: string;
  isOpen: boolean;
  onToggle: () => void;
  detail: DetailRow[];
}

function BarRow({ label, color, labelColor, borderColor, amount, pct, formula, isOpen, onToggle, detail }: BarRowProps) {
  return (
    <div className="rounded-xl bg-foreground-900/40 border border-foreground-800/60 overflow-hidden">
      {/* Header row — tap to toggle */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${color} flex-shrink-0`} />
          <span className="text-[13px] font-[650] text-background-50">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-[750] text-background-50">${amount.toFixed(0)}</span>
          <span className="text-[11px] text-foreground-500">({pct}%)</span>
          <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Bar */}
      <div className="px-4 pb-3">
        <div className="relative w-full h-7 bg-foreground-800 rounded-md overflow-hidden">
          <div
            className={`h-full ${color} rounded-md transition-all duration-700 ease-out flex items-center`}
            style={{ width: `${Math.max(pct, 4)}%` }}
          >
            {pct >= 20 && (
              <span className="text-[11px] font-bold text-background-50 ml-3">{pct}%</span>
            )}
          </div>
        </div>
        <p className="text-[11px] text-foreground-600 mt-1.5">{formula}</p>
      </div>

      {/* Expandable detail */}
      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className={`mx-4 mb-4 rounded-lg p-3.5 bg-foreground-950/60 border ${borderColor}`}>
            <span className={`text-[10px] font-[650] ${labelColor} uppercase tracking-wider block mb-3`}>
              Step-by-step
            </span>
            <div className="space-y-2">
              {detail.map((row, i) => (
                <div key={i} className={`flex items-center justify-between ${row.highlight ? 'pt-2 border-t border-foreground-800' : ''}`}>
                  <span className="text-[12px] text-foreground-400 pr-3">{row.label}</span>
                  <span className={`text-[12px] font-[700] flex-shrink-0 ${row.highlight ? labelColor : 'text-background-50'}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InsightBlock({ commissionPct }: { commissionPct: number }) {
  const text = commissionPct >= 60
    ? 'Commission dominates your cost structure — negotiating a lower commission rate is where the real savings are.'
    : commissionPct >= 30
      ? 'Commission and spread are roughly balanced — optimize both to meaningfully reduce total trading cost.'
      : 'Spread is the dominant cost here — even a 0.1 pip improvement saves more than a 20% commission discount.';

  return (
    <div className="flex items-start gap-3 p-3.5 md:p-4 rounded-xl bg-background-50/5 border border-background-50/10">
      <i className="ri-lightbulb-line w-4 h-4 md:w-5 md:h-5 flex items-center justify-center text-accent-400 mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-[10px] md:text-[11px] font-[650] text-foreground-400 uppercase tracking-wider">Key Insight</span>
        <p className="text-[13px] md:text-[14px] font-[600] text-background-50 mt-1 leading-snug">{text}</p>
      </div>
    </div>
  );
}